import { css, useTheme } from '@emotion/react';
import type { getInitialGitProvider } from 'utils/vcs.utils';
import { ReactComponent as RawHelmIcon } from 'assets/images/helm-logo-on-white.svg';
import styled from 'types/theme.types';
import { ReactComponent as RawBitbucketIcon } from 'assets/images/bitbucket.svg';
import { ReactComponent as RawGitlabIcon } from 'assets/images/gitlab.svg';
import { ReactComponent as RawGitIcon } from 'assets/images/git.svg';
import { ReactComponent as RawAzureDevOpsIcon } from 'assets/images/azure-devops.svg';
import { GithubFilled, GithubOutlined } from '@ant-design/icons';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';

interface Props {
  providerName: ReturnType<typeof getInitialGitProvider>;
}

/* The margin nudges are legacy compensation hand-tuned against the pre-restyle avatar box; on the new
   design they pushed every glyph below the Avatar's own centring. The new design sizes the glyph
   instead of scaling it after layout. The legacy Filled render is pinned by parity, which keeps this
   file on the outlined-icons allowlist until APO-484. */
const VcsIcon = ({ providerName }: Props) => {
  const { isNewDesign } = useTheme();
  const github = isNewDesign ? <NewDesignGithubIcon /> : <CenteredGithubIcon />;

  if (!providerName) return <GitIcon />;
  return {
    BitBucket: <CenteredBitbucketIcon />,
    BitBucketServer: <CenteredBitbucketIcon />,
    GitHub: github,
    GitHubEnterprise: github,
    GitLab: <CenteredGitlabIcon />,
    AzureDevOps: <CenteredAzureDevopsIcon />,
    HelmRepository: <CenteredHelmRepositoryIcon />,
    GitLabEnterprise: <CenteredGitlabIcon />,
    Other: <GitIcon />
  }[providerName];
};

export default VcsIcon;

const newDesignGlyphSize = newDesignOnly(css`
  width: 16px;
  height: 16px;
`);

const CenteredBitbucketIcon = styled(RawBitbucketIcon)`
  ${legacyOnly(css`
    margin-top: 5px;
    // Make the logo proportional to the avatar size
    transform: scale(0.8);
  `)}
  ${newDesignGlyphSize}
`;

const CenteredGithubIcon = styled(GithubFilled)`
  margin-top: 3px;
  transform: scale(0.8);
  color: ${({ theme }) => theme.primaryBlack};
`;

const NewDesignGithubIcon = styled(GithubOutlined)`
  font-size: 16px;
  color: var(--icon-primary);
`;

const CenteredGitlabIcon = styled(RawGitlabIcon)`
  ${legacyOnly(css`
    margin-top: 4px;
    // Make the logo proportional to the avatar size
    transform: scale(0.8);
  `)}
  ${newDesignGlyphSize}
`;

const CenteredAzureDevopsIcon = styled(RawAzureDevOpsIcon)`
  ${legacyOnly(css`
    margin-top: 3px;
    margin-right: 1px;
    // Make the logo proportional to the avatar size
    transform: scale(0.8);
  `)}
  ${newDesignGlyphSize}
`;

const CenteredHelmRepositoryIcon = styled(RawHelmIcon)`
  ${legacyOnly(css`
    // Make the logo proportional to the avatar size
    transform: scale(0.75);
  `)}
  ${newDesignGlyphSize}
`;

const GitIcon = styled(RawGitIcon)`
  ${legacyOnly(css`
    margin-top: 6px;
  `)}
  ${newDesignGlyphSize}
`;
